import * as React from 'react'
import { ChartLine, Pulse, CurrencyDollar, TrendUp } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Card, CardHeader, CardTitle, CardAction, CardContent } from '../core/card'
import { TabRoot, TabList, TabTrigger, TabContent } from '../core/tabs'
import { type PeriodValue, type PeriodQuickSelect } from '../core/period-picker'
import {
  TrendChart,
  LineChart,
  PerformanceControls,
  type PerformanceTrends,
  type LineChartData,
} from './performance-charts'

/**
 * TrendsWidget — the three trend charts (Activity · Value · Pipeline) combined
 * into one card, switched by an accessible tab bar and sharing a single
 * `PeriodPicker` in the header. The space-saving alternative to the three
 * standalone {@link ActivityTrendWidget} / {@link ValueTrendWidget} /
 * {@link PipelineTrendWidget} cards — one footprint instead of three, which
 * suits a homepage that can't yet hide or rearrange widgets.
 *
 *   <TrendsWidget
 *     activity={activityTrends}
 *     value={valueLineData}
 *     pipeline={pipelineLineData}
 *     onPeriodApply={reload}
 *   />
 *
 * Each tab reuses the same renderer as its standalone widget: Activity is the
 * grouped bar {@link TrendChart}; Value and Pipeline are the {@link LineChart}
 * (Pipeline dual-axis). The period applies across all three tabs.
 */

export type TrendsTab = 'activity' | 'value' | 'pipeline'

export interface TrendsWidgetProps {
  /** Activity volume over time (grouped bars). */
  activity: PerformanceTrends
  /** Deal value over time (line). */
  value: LineChartData
  /** Pipeline value + count over time (dual-axis line). */
  pipeline: LineChartData
  /** Initially selected tab. Defaults to `'activity'`. */
  defaultTab?: TrendsTab
  /** Called when the active tab changes. */
  onTabChange?: (tab: TrendsTab) => void
  periodLabel?: React.ReactNode
  defaultPeriod?: PeriodValue
  onPeriodApply?: (value: PeriodValue) => void
  /** Override the period picker's quick-selects (defaults to the relative "last N" set). */
  quickSelects?: PeriodQuickSelect[]
  /** Hide the picker's fiscal mode toggle — calendar + quick-selects only. */
  showModes?: boolean
  /** Wire the ⋯ menu's "Hide widget" action; hidden when omitted. */
  onHide?: () => void
  title?: React.ReactNode
  className?: string
}

// `flex min-h-0 flex-1 flex-col` gives the active panel a definite height so the
// chart fills it. But `display:flex` (an author style) overrides the `hidden`
// attribute `TabContent` sets on inactive panels (`display:none` is only a UA
// style), so all three would render at once — `[&[hidden]]:hidden` restores the
// hide with a higher-specificity author rule (`.panel[hidden]` beats `.flex`).
const chartPanelClass = 'flex min-h-0 flex-1 flex-col [&[hidden]]:hidden'

export const TrendsWidget = React.forwardRef<HTMLDivElement, TrendsWidgetProps>(
  (
    {
      activity,
      value,
      pipeline,
      defaultTab = 'activity',
      onTabChange,
      periodLabel = 'Period',
      defaultPeriod,
      onPeriodApply,
      quickSelects,
      showModes,
      onHide,
      title = 'Trends',
      className,
    },
    ref
  ) => (
    <Card ref={ref} className={cn('flex w-full max-w-2xl flex-col', className)}>
      {/* Tab bar reads as sub-nav under the title → pull it close to the header
          (trim the header's bottom padding); the chart gets its breathing room
          below the tabs via the TabRoot gap. */}
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <ChartLine aria-hidden="true" className="size-4 text-fbui-gray-500" />
          {title}
        </CardTitle>
        <CardAction>
          <PerformanceControls
            periodLabel={periodLabel}
            defaultPeriod={defaultPeriod}
            onPeriodApply={onPeriodApply}
            quickSelects={quickSelects}
            showModes={showModes}
            onHide={onHide}
            hideLabel="Trends options"
          />
        </CardAction>
      </CardHeader>
      {/* The tab bar + active chart fill the remaining card height. */}
      <CardContent className="flex min-h-0 flex-1 flex-col">
        <TabRoot
          variant="minimal"
          size="md"
          defaultValue={defaultTab}
          onValueChange={(v) => onTabChange?.(v as TrendsTab)}
          className="min-h-0 flex-1 gap-[1.25rem]"
        >
          <TabList aria-label="Trend metric">
            <TabTrigger value="activity" icon={<Pulse />}>
              Activity
            </TabTrigger>
            <TabTrigger value="value" icon={<CurrencyDollar />}>
              Value
            </TabTrigger>
            <TabTrigger value="pipeline" icon={<TrendUp />}>
              Pipeline
            </TabTrigger>
          </TabList>
          <TabContent value="activity" className={chartPanelClass}>
            <TrendChart trends={activity} />
          </TabContent>
          <TabContent value="value" className={chartPanelClass}>
            <LineChart data={value} />
          </TabContent>
          <TabContent value="pipeline" className={chartPanelClass}>
            <LineChart data={pipeline} />
          </TabContent>
        </TabRoot>
      </CardContent>
    </Card>
  )
)
TrendsWidget.displayName = 'TrendsWidget'
